import { useEffect, useState } from "react";

import { CircleHelp, UserRound } from "lucide-react";

import {
  isPublicAnalyticsLoaded,
  syncPublicAnalytics,
} from "./analytics/publicAnalytics";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { errorMessage } from "./components/ErrorAlert";
import { BasicFlowModal, takeBasicFlowTurn } from "./guide/BasicFlowModal";
import { GuideModal } from "./guide/GuideModal";
import { readGuideState } from "./guide/guideStorage";
import { InitialPasswordWarning } from "./components/InitialPasswordWarning";
import { installErrorCapture } from "./feedback/clientLog";
import { Link } from "./components/Link";
import { Logo } from "./components/Logo";
import { ManualMenu } from "./components/ManualMenu";
import { NotificationBell } from "./components/NotificationBell";
import { AccountPage } from "./pages/AccountPage";
import { AdminAuditPage } from "./pages/AdminAuditPage";
import { AdminBetaApplicationsPage } from "./pages/AdminBetaApplicationsPage";
import { AdminDashboardPage } from "./pages/AdminDashboardPage";
import { AdminGalleryPage } from "./pages/AdminGalleryPage";
import { AdminLivePage } from "./pages/AdminLivePage";
import { AdminReportsPage } from "./pages/AdminReportsPage";
import { AdminTeacherActivityPage } from "./pages/AdminTeacherActivityPage";
import { AdminTeachersPage } from "./pages/AdminTeachersPage";
import { ClassDetailPage } from "./pages/ClassDetailPage";
import { ClassHandoutPage } from "./pages/ClassHandoutPage";
import { ClassListPage } from "./pages/ClassListPage";
import { GalleryPage } from "./pages/GalleryPage";
import { GuidePage } from "./pages/GuidePage";
import { ManualPage } from "./pages/ManualPage";
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { NameSetupPage } from "./pages/NameSetupPage";
import { PasswordPage } from "./pages/PasswordPage";
import { RevisionPage } from "./pages/RevisionPage";
import { SchoolSetupPage } from "./pages/SchoolSetupPage";
import { StudentManualPage } from "./pages/StudentManualPage";
import { UnsubscribePage } from "./pages/UnsubscribePage";
import {
  NavigationContext,
  classPath,
  parseBetaStatus,
  parseClassTab,
  useLocationRoute,
} from "./router";
import { applyDocumentHead } from "./seo/documentHead";

import "./styles.scss";

import type { CanvaschoolApi } from "./api/client";
import type { AuthState } from "./auth/AuthProvider";
import type { CatalogTransportFactory } from "./catalog/catalogTransport";
import type { EditorRoute, Route } from "./router";
import type { AuthSessionEnvelope } from "@canvaschool/contracts";
import type { ReactNode } from "react";

export type CanvaschoolShellProps = {
  api: CanvaschoolApi;
  createCatalogTransport?: CatalogTransportFactory;
  /** Task 11 renders the authenticated editor for canvas routes. */
  renderEditor?: (route: EditorRoute) => ReactNode;
};

const ROLE_LABEL = {
  ADMIN: "관리자",
  TEACHER: "교사",
  STUDENT: "학생",
} as const;

const homeFor = (session: AuthSessionEnvelope): string => {
  switch (session.principal.role) {
    case "ADMIN":
      return "/admin";
    case "TEACHER":
      return "/classes";
    default:
      return session.studentProfile
        ? classPath(session.studentProfile.classId)
        : "/name";
  }
};

/** A student without a known name (or profile) must set one first. */
const needsName = (session: AuthSessionEnvelope) =>
  session.principal.role === "STUDENT" &&
  (!session.studentProfile || session.studentProfile.displayName === null);

/**
 * A teacher whose server reports no school must pick one first. An older
 * server sends no teacherProfile at all; then nothing is gated.
 */
const needsSchool = (session: AuthSessionEnvelope) =>
  session.principal.role === "TEACHER" &&
  session.teacherProfile !== undefined &&
  session.teacherProfile.school === null;

/** The picture manual and its student handout are public to everyone. */
const isManual = (route: Route) =>
  route.name === "manual" || route.name === "studentManual";

/** Where the current route must redirect for this session, if anywhere. */
const redirectFor = (state: AuthState, route: Route): string | null => {
  if (state.status === "loading" || state.status === "unavailable") {
    return null;
  }
  // Opened from a mail; the link itself is the authority, not the session.
  if (route.name === "unsubscribe") {
    return null;
  }
  if (state.status === "anonymous") {
    // /beta was the open-beta application; teachers now sign in directly.
    return route.name === "login" || route.name === "home" || isManual(route)
      ? null
      : "/login";
  }
  const { session } = state;
  const role = session.principal.role;
  if (needsName(session)) {
    return route.name === "nameSetup" ? null : "/name";
  }
  if (needsSchool(session)) {
    // Changing the password and signing out (on the account page) stay
    // possible meanwhile.
    return route.name === "school" ||
      route.name === "password" ||
      route.name === "account"
      ? null
      : "/school";
  }
  if (
    route.name === "home" ||
    route.name === "login" ||
    route.name === "beta" ||
    route.name === "nameSetup"
  ) {
    return homeFor(session);
  }
  if (role === "ADMIN") {
    return route.name === "adminHome" ||
      route.name === "adminAudit" ||
      route.name === "adminReports" ||
      route.name === "adminTeacherActivity" ||
      route.name === "adminLive" ||
      route.name === "adminTeachers" ||
      route.name === "adminBetaApplications" ||
      route.name === "adminGallery" ||
      route.name === "gallery" ||
      isManual(route) ||
      route.name === "password" ||
      route.name === "account"
      ? null
      : "/admin";
  }
  if (
    route.name === "adminHome" ||
    route.name === "adminAudit" ||
    route.name === "adminReports" ||
    route.name === "adminTeacherActivity" ||
    route.name === "adminLive" ||
    route.name === "adminTeachers" ||
    route.name === "adminBetaApplications" ||
    route.name === "adminGallery"
  ) {
    return homeFor(session);
  }
  // Only the teacher may change their own password.
  if (session.impersonator && route.name === "password") {
    return homeFor(session);
  }
  if (role !== "TEACHER" && route.name === "school") {
    return homeFor(session);
  }
  if (role === "STUDENT" && route.name === "classes") {
    return homeFor(session);
  }
  return null;
};

const Forbidden = () => (
  <p className="canvaschool-error" role="alert">
    이 페이지를 볼 권한이 없습니다.
  </p>
);

const ShellRoutes = ({
  createCatalogTransport,
  renderEditor,
}: Pick<CanvaschoolShellProps, "createCatalogTransport" | "renderEditor">) => {
  const { route, search, navigate } = useLocationRoute();
  const auth = useAuth();
  const { state } = auth;
  const redirect = redirectFor(state, route);
  const [guideOpen, setGuideOpen] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);

  // A teacher's own pages open the basic flow once per login. An
  // administrator inside the account would only dismiss it for the teacher.
  const flowUserId =
    state.status === "authenticated" &&
    !redirect &&
    state.session.principal.role === "TEACHER" &&
    !state.session.impersonator &&
    (route.name === "classes" || route.name === "class")
      ? state.session.principal.userId
      : null;

  useEffect(() => {
    if (
      flowUserId &&
      takeBasicFlowTurn(flowUserId, readGuideState(flowUserId).dismissedCards)
    ) {
      setFlowOpen(true);
    }
  }, [flowUserId]);

  // Uncaught errors reach operators on every page, signed in or not: the
  // login and landing pages can break too.
  useEffect(() => installErrorCapture(auth.api.sendClientReport), [auth.api]);

  useEffect(() => {
    applyDocumentHead(route.name);
  }, [route.name]);

  // "/" shows the login form instead of the landing page after an account
  // error; that is not a public page view.
  const measuredRoute =
    route.name === "home" &&
    state.status === "anonymous" &&
    (state.error || state.notice)
      ? null
      : route.name;

  // Visit statistics for the signed-out public pages only (after the head,
  // so the page title is current).
  useEffect(() => {
    syncPublicAnalytics({ routeName: measuredRoute, authStatus: state.status });
  }, [measuredRoute, state.status]);

  useEffect(() => {
    if (state.status === "authenticated" && isPublicAnalyticsLoaded()) {
      // A public page loaded the measurement script; signing in reloads the
      // page so it does not stay in memory on classroom pages.
      window.location.replace(redirect ?? window.location.href);
      return;
    }
    if (redirect && redirect !== window.location.pathname) {
      // Redirects follow the session; the user has no choice to confirm.
      navigate(redirect, { replace: true, force: true });
    }
  }, [redirect, navigate, state.status]);

  if (state.status === "loading") {
    return (
      <div className="canvaschool-shell" aria-busy="true">
        <p>불러오는 중…</p>
      </div>
    );
  }
  if (state.status === "unavailable") {
    return (
      <div className="canvaschool-shell">
        <main className="canvaschool-main">
          <p className="canvaschool-error" role="alert">
            {errorMessage(state.error)}
          </p>
          <button type="button" onClick={() => void auth.reloadSession()}>
            다시 시도
          </button>
        </main>
      </div>
    );
  }

  const page = (): ReactNode => {
    if (state.status !== "authenticated") {
      // 계정 상태 오류나 비밀번호 변경 안내는 로그인 화면에서만 보여 준다.
      if (route.name === "home" && !state.error && !state.notice) {
        return <LandingPage />;
      }
      if (route.name === "unsubscribe") {
        return <UnsubscribePage search={search} />;
      }
      if (route.name === "manual") {
        return <ManualPage />;
      }
      if (route.name === "studentManual") {
        return <StudentManualPage />;
      }
      return (
        <LoginPage initialError={state.error} notice={state.notice ?? null} />
      );
    }
    if (redirect) {
      return null;
    }
    const role = state.session.principal.role;
    switch (route.name) {
      case "nameSetup":
        return <NameSetupPage />;
      case "school":
        return <SchoolSetupPage />;
      case "password":
        return <PasswordPage />;
      case "account":
        return <AccountPage />;
      case "guide":
        return <GuidePage />;
      case "manual":
        return <ManualPage signedIn />;
      case "studentManual":
        return <StudentManualPage />;
      case "unsubscribe":
        return <UnsubscribePage search={search} />;
      case "gallery":
        return <GalleryPage />;
      case "adminGallery":
        return <AdminGalleryPage />;
      case "adminHome":
        return <AdminDashboardPage />;
      case "adminAudit":
        return <AdminAuditPage />;
      case "adminReports":
        return <AdminReportsPage />;
      case "adminTeacherActivity":
        return <AdminTeacherActivityPage />;
      case "adminLive":
        return <AdminLivePage />;
      case "adminTeachers":
        return <AdminTeachersPage />;
      case "adminBetaApplications":
        return (
          <AdminBetaApplicationsPage
            key={search}
            initialStatus={parseBetaStatus(search)}
          />
        );
      case "classes":
        return <ClassListPage />;
      case "class":
        return (
          <ClassDetailPage
            key={route.classId}
            classId={route.classId}
            tab={parseClassTab(search)}
            createCatalogTransport={createCatalogTransport}
          />
        );
      case "classHandout":
        return role === "TEACHER" ? (
          <ClassHandoutPage
            key={route.classId}
            classId={route.classId}
            autoPrint={new URLSearchParams(search).has("print")}
          />
        ) : (
          <Forbidden />
        );
      case "canvas":
        return renderEditor ? (
          renderEditor({ classId: route.classId, canvasId: route.canvasId })
        ) : (
          <p>편집기를 준비하고 있습니다.</p>
        );
      case "revisions":
        return role === "TEACHER" ? (
          <RevisionPage
            key={`${route.classId}:${route.canvasId}`}
            classId={route.classId}
            canvasId={route.canvasId}
          />
        ) : (
          <Forbidden />
        );
      case "notFound":
        return (
          <p className="canvaschool-error" role="alert">
            페이지를 찾을 수 없습니다.
          </p>
        );
      default:
        return null;
    }
  };

  const session = state.status === "authenticated" ? state.session : null;
  const studentNumber = session?.studentProfile?.studentNumber;
  const myClassesPath =
    session?.principal.role === "TEACHER"
      ? "/classes"
      : session?.principal.role === "STUDENT" && session.studentProfile
      ? classPath(session.studentProfile.classId)
      : null;
  const showHeader =
    Boolean(session) &&
    route.name !== "canvas" &&
    route.name !== "studentManual" &&
    route.name !== "unsubscribe" &&
    route.name !== "classHandout";
  const landing =
    state.status === "anonymous" &&
    (route.name === "home" || route.name === "login");

  return (
    <NavigationContext.Provider value={navigate}>
      <div className="canvaschool-shell">
        {showHeader && session?.impersonator && (
          <div className="canvaschool-impersonation" role="status">
            <p>
              관리자({session.impersonator.loginId})로{" "}
              <strong>{session.principal.loginId}</strong> 선생님의 화면을 보고
              있습니다. 여기서 하는 일은 선생님 계정으로 처리됩니다.
            </p>
            <button
              type="button"
              onClick={async () => {
                try {
                  await auth.endImpersonation();
                } catch {
                  // The session reload that follows decides where to go.
                  return;
                }
                navigate("/admin/teachers", { replace: true, force: true });
              }}
            >
              관리자로 돌아가기
            </button>
          </div>
        )}
        {showHeader && session && (
          <header
            className={`canvaschool-header canvaschool-header--${session.principal.role.toLowerCase()}`}
          >
            <div className="canvaschool-header-inner">
              <div className="canvaschool-header-meta">
                <span className="canvaschool-brand">
                  <Logo inverted size={22} />
                  크루바스
                </span>
                <span className="canvaschool-role">
                  {ROLE_LABEL[session.principal.role]}
                </span>
                {session.principal.role === "STUDENT" && studentNumber && (
                  <p className="canvaschool-identity" role="status">
                    현재 {studentNumber}번으로 로그인했습니다.
                  </p>
                )}
              </div>
              <div className="canvaschool-header-nav">
                <div
                  className={
                    session.principal.role === "ADMIN"
                      ? "canvaschool-actions canvaschool-actions--admin"
                      : "canvaschool-actions"
                  }
                >
                  {myClassesPath && (
                    <Link
                      to={myClassesPath}
                      aria-current={
                        route.name === "classes" || route.name === "class"
                          ? "page"
                          : undefined
                      }
                    >
                      나의 학급
                    </Link>
                  )}
                  {session.principal.role === "ADMIN" && (
                    <>
                      <Link to="/admin">대시보드</Link>
                      <Link to="/admin/teachers">교사 계정</Link>
                      <Link to="/admin/teacher-activity">교사 이용 현황</Link>
                      <Link to="/admin/live">실시간 관제</Link>
                      <Link to="/admin/beta-applications">베타 신청</Link>
                      <Link to="/admin/audit">감사 기록</Link>
                      <Link to="/admin/gallery">갤러리 관리</Link>
                      <Link to="/admin/reports">의견·제보</Link>
                    </>
                  )}
                  {session.principal.role !== "ADMIN" && (
                    <Link to="/gallery">학교 밖 갤러리</Link>
                  )}
                  {session.principal.role === "TEACHER" && <ManualMenu />}
                  {session.principal.role !== "ADMIN" && (
                    <button
                      type="button"
                      className="canvaschool-guide-link"
                      onClick={() => setGuideOpen(true)}
                    >
                      <CircleHelp size={16} aria-hidden="true" />
                      사용 가이드
                    </button>
                  )}
                  {session.principal.role !== "ADMIN" && (
                    <NotificationBell
                      api={auth.api}
                      refreshKey={`${session.principal.userId}:${window.location.pathname}`}
                    />
                  )}
                </div>
                <Link
                  className="canvaschool-header-user"
                  to="/account"
                  aria-label={`${session.principal.loginId} 마이페이지`}
                  aria-current={route.name === "account" ? "page" : undefined}
                >
                  <UserRound size={16} aria-hidden="true" />
                  <span className="canvaschool-header-user-id">
                    {session.principal.loginId}
                  </span>
                </Link>
              </div>
            </div>
          </header>
        )}
        {showHeader && guideOpen && (
          <GuideModal onClose={() => setGuideOpen(false)} />
        )}
        {flowOpen && flowUserId && (
          <BasicFlowModal
            userId={flowUserId}
            onClose={() => setFlowOpen(false)}
          />
        )}
        {showHeader && session?.initialPasswordWarning && (
          <InitialPasswordWarning
            key={session.principal.sessionId}
            sessionId={session.principal.sessionId}
            userId={session.principal.userId}
          />
        )}
        <main
          className={
            route.name === "canvas"
              ? "canvaschool-main canvaschool-main--canvas"
              : (landing && route.name === "home") ||
                isManual(route) ||
                route.name === "unsubscribe" ||
                route.name === "classHandout"
              ? "canvaschool-main canvaschool-main--landing"
              : "canvaschool-main"
          }
        >
          {page()}
        </main>
      </div>
    </NavigationContext.Provider>
  );
};

export const CanvaschoolShell = ({
  api,
  createCatalogTransport,
  renderEditor,
}: CanvaschoolShellProps) => (
  <AuthProvider api={api}>
    <ShellRoutes
      createCatalogTransport={createCatalogTransport}
      renderEditor={renderEditor}
    />
  </AuthProvider>
);
